import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RedeEspecialService } from '../rede-especial.service';
import { formatDate } from '@angular/common';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { SelectionModel } from '@angular/cdk/collections';
import { CustomPaginatorIntl } from 'src/app/custom-paginator-intl';

/**
 * Interface para elementos de contingência
 */
export interface ContingencyElement {
  codPosto: number;
  nomePosto: string;
}

@Component({
  selector: 'app-busca-posto-dialog',
  standalone: true,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatSortModule,
    MatListModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatDividerModule,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }
  ],
  templateUrl: './busca-posto-dialog.component.html',
  styleUrls: [
    './busca-posto-dialog.component.scss',
    '../../shared/styles/dialog.scss',
  ],
})

export class BuscaPostoDialogComponent {
  hiddenTable: boolean = true;
  disabledButton: boolean = true;

  // Fonte de dados da tabela com Material
  items = new MatTableDataSource<any>([]);

  // Referência para ordenação da tabela
  @ViewChild(MatSort) sort: any = MatSort;

  // Referência para paginação da tabela
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Gerenciador de seleção de linhas da tabela
  selection = new SelectionModel<ContingencyElement>(true, []);

  // Colunas exibidas na tabela
  displayedColumns: string[] = [];
  params: any;
  isLoading: boolean = false;
  hiddenAlert: boolean = true;
  postoSelected = 'codPosto';

  // Cabeçalhos das colunas
  headerColums: string[] = [
    'chk',
    'Cód. Posto', 
    'Nome Posto', 
  ];

  formSearch: any = UntypedFormGroup;

  constructor(
    //public dialogRef: MatDialogRef<BuscaAcordoComponent>,
    @Inject(MAT_DIALOG_DATA) params: any,
    private service: RedeEspecialService,
    private fb: UntypedFormBuilder,
    private _liveAnnouncer: LiveAnnouncer
  ) {
    this.params = params;
  }

  ngOnInit(): void {
    this.gerarForm();

    // Monitora mudanças no paramType
    this.formSearch.get('paramType')?.valueChanges.subscribe((value: any) => {
      if (value) {
        this.formSearch.get('paramValue')?.enable(); // Habilita o campo

        // Verifica se o tipo selecionado é numérico (codFrota)
        if (value === 'codPosto') {
          // Configura o campo para aceitar apenas números
          this.formSearch
            .get('paramValue')
            ?.setValidators([
              Validators.required,
              Validators.minLength(1),
              Validators.pattern(/^\d+$/),
            ]);
        } else {
          // Para outros tipos, mantém apenas as validações básicas
          this.formSearch
            .get('paramValue')
            ?.setValidators([Validators.required, Validators.minLength(1)]);
        }
        // Atualiza as validações
        this.formSearch.get('paramValue')?.updateValueAndValidity();
      } else {
        this.formSearch.get('paramValue')?.disable(); // Desabilita caso esteja vazio
      }
    });

    // Monitora mudanças no paramValue para habilitar o botão de limpar
    this.formSearch.get('paramValue')?.valueChanges.subscribe((value: any) => {
      // Só habilita o botão limpar se tiver algo digitado no campo de parâmetro
      this.disabledButton = !value || value.trim().length === 0;
    });
    
    this.initAllSearch();
  }

  /**
   * Configuração após a inicialização da view
   */
  ngAfterViewInit() {
    this.items.sort = this.sort;
    this.items.paginator = this.paginator;
  }

  gerarForm() {
    this.formSearch = this.fb.group({
      paramType: ['', [Validators.required, Validators.minLength(1)]],
      paramValue: [
        { value: '', disabled: true },
        [Validators.required, Validators.minLength(1)],
      ],
    });
  }

  search() {
    
    this.hiddenTable = true;
    this.isLoading = true;
    // Verifica se o formulário é válido antes de fazer a chamada ao serviço
    if (this.formSearch.invalid) {
      console.log("search1");
      // Exibe mensagem de erro e desabilita o botão de pesquisa
      /* this.snackBar.open('Por favor, preencha todos os campos corretamente antes de pesquisar.', 'Fechar', {
        duration: 5000,
        panelClass: ['mat-snackbar-error']
      }); */
      // Marca todos os campos como tocados para exibir as mensagens de erro
      Object.keys(this.formSearch.controls).forEach((key) => {
        const control = this.formSearch.get(key);
        control?.markAsTouched(); // Marca o campo como "tocado" para ativar a exibição das mensagens de erro de validação
        // Marca o campo como "sujo" para indicar que seu valor foi alterado pelo usuário,
        // o que também ativa a validação e exibição de mensagens de erro
        control?.markAsDirty();
      });
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.service
      .buscaPostoParametro(
        this.formSearch.value.paramType,
        this.formSearch.value.paramValue
      )
      .subscribe({
        next: (data: any) => {
          this.items.data = data.retorno;
          this.items.sort = this.sort;
          this.items.paginator = this.paginator;
          this.displayedColumns = ['chk','codPosto', 'nomePosto'];
          if (this.items.data.length === 0) {
            this.hiddenTable = true;
            this.hiddenAlert = false;
            this.isLoading = false;
          } else {
            this.hiddenTable = false;
            this.hiddenAlert = true;
            this.isLoading = false;
          }
        },
        error: (err: any) => {
          console.log(err);
          this.isLoading = false;
        },
      });
  }

  /**
   * Seleciona um posto e envia os dados para o componente pai
   */
  detail(codPosto: number, nomePosto: string) {
    this.params.callback(codPosto, nomePosto);
  }

  clean() {
    // Limpa os campos do formulário
    this.formSearch.get('paramValue')?.reset();

    // Desabilita o botão de limpar
    this.disabledButton = true;

    // Reseta a tabela e mensagens
    this.hiddenTable = true;
    this.hiddenAlert = true;
    this.items = new MatTableDataSource();

    this.initAllSearch();
  }

  enableButton() {
    // Habilita o botão de limpar se algum campo tiver valor
    const paramTypeValue = this.formSearch.get('paramType')?.value || '';
    const paramValueValue = this.formSearch.get('paramValue')?.value || '';

    this.disabledButton = !(
      paramTypeValue.length > 0 || paramValueValue.length > 0
    );
  }

  /**
   * Anuncia mudanças na ordenação para acessibilidade
   * @param sortState Estado atual da ordenação
   */
  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  initAllSearch() {
    this.hiddenTable = true;
    this.isLoading = true;
    this.isLoading = true;
    this.service
      .buscaPostoParametro(
        '',''
      )
      .subscribe({
        next: (data: any) => {
          this.items.data = data.retorno;
          this.items.sort = this.sort;
          this.items.paginator = this.paginator;
          this.displayedColumns = ['chk','codPosto', 'nomePosto'];
          if (this.items.data.length === 0) {
            this.hiddenTable = true;
            this.hiddenAlert = false;
            this.isLoading = false;
          } else {
            this.hiddenTable = false;
            this.hiddenAlert = true;
            this.isLoading = false;
          }
        },
        error: (err: any) => {
          console.log(err);
          this.isLoading = false;
        },
      });
  }

  exportTOExcel() {
    // 1. Remove a coluna do checkbox
    const exportColumns = this.displayedColumns.filter(col => col !== 'chk');
  
    // 2. Filtra os headers com base nos índices das colunas válidas
    const exportHeaders = this.headerColums.filter((_, i) => this.displayedColumns[i] !== 'chk');
  
    // 3. Monta os dados formatados
    const xlsx = this.items.data.map((a: any) => {
      return exportColumns.map(c => {
        let value = a[c];
        return value;
      });
    });
  
    // 4. Adiciona os headers
    xlsx.unshift(exportHeaders);
  
    // 5. Cria o Excel
    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(xlsx);
  
    const workbook: XLSX.WorkBook = {
      Sheets: { Postos_01: worksheet },
      SheetNames: ['Postos_01']
    };
  
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const now = new Date();
    const formattedDate =
      String(now.getDate()).padStart(2, '0') +'-'+ String(now.getMonth() + 1).padStart(2, '0') +'-'+ now.getFullYear() 
      +'_'+ String(now.getHours()).padStart(2, '0') +'-'+ String(now.getMinutes()).padStart(2, '0');

    const fileName = 'Postos_' + formattedDate + '.xlsx';

    this.save(excelBuffer, fileName);
  }

    save(buffer: any, fileName: string) {
    const data: Blob = new Blob([buffer], {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    FileSaver.saveAs(data, fileName);
  }

  /**
   * Alterna a seleção de todas as linhas
   */
  masterToggle(event: any) {  
    const skip = this.paginator.pageSize * this.paginator.pageIndex;

    const pagedData = this.items.sortData(this.items.data, this.sort).filter((u, i) => i >= skip)
    .filter((u, i) => i <this.paginator.pageSize);
    this.items.paginator = this.paginator;
    
    pagedData.forEach((row: any) => {
        if ( event.checked ) {
            this.selection.select(row)
        }
        else {
          this.selection.clear();
        }
    });
  }

  importSelecteds() {
 
    this.params.callback(this.selection.selected);
  }
}
